import { useNotificationStore } from './store';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { styled, keyframes } from '@mui/material/styles';
import { useEffect, useState } from 'react';

const slideAnimation = keyframes`
  0% { transform: translateX(-100%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const slideOutAnimation = keyframes`
  0% { transform: translateX(0); opacity: 1; }
  100% { transform: translateX(-100%); opacity: 0; }
`;

const SnackbarContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 16,
  left: 16,
  zIndex: 1400,
  display: 'flex',
  flexDirection: 'column-reverse',
  gap: theme.spacing(1),
  maxWidth: 400,
  pointerEvents: 'none',
}));

const SnackbarItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExiting' && prop !== 'index',
})<{ isExiting?: boolean; index: number }>(({ theme, isExiting, index }) => ({
  pointerEvents: 'auto',
  animation: `${isExiting ? slideOutAnimation : slideAnimation} 0.3s ease-in-out`,
  transform: `scale(${Math.max(0.96, 1 - index * 0.02)})`,
  opacity: Math.max(0.8, 1 - index * 0.08),
  transition: theme.transitions.create(['transform', 'opacity'], {
    duration: theme.transitions.duration.standard,
  }),
  '&:hover': {
    transform: `translateX(8px) scale(${Math.max(0.96, 1 - index * 0.02)})`,
    opacity: 1,
  },
}));

export default function Snackbar() {
  const { snackbarQueue, removeSnackbar } = useNotificationStore();
  const [exitingItems, setExitingItems] = useState<Set<string>>(new Set());

  const handleClose = (id: string) => {
    setExitingItems((prev) => new Set(Array.from(prev).concat(id)));
    setTimeout(() => {
      removeSnackbar(id);
      setExitingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300);
  };

  // Clean up exiting items that no longer exist
  useEffect(() => {
    setTimeout(() => {
      const currentIds = new Set(snackbarQueue.map((n) => n.id));
      setExitingItems((prev) => new Set(Array.from(prev).filter((id) => currentIds.has(id))));
    }, 0);
  }, [snackbarQueue]);

  return (
    <SnackbarContainer>
      {snackbarQueue.map((notification, index) => (
        <SnackbarItem
          key={notification.id}
          index={index}
          isExiting={exitingItems.has(notification.id!)}
        >
          <Alert
            severity={notification.severity as 'error' | 'warning' | 'info' | 'success'}
            variant='filled'
            onClose={
              notification.mode === 'loading' ? undefined : () => handleClose(notification.id!)
            }
            icon={
              notification.mode === 'loading' ? (
                <CircularProgress size={20} color='inherit' />
              ) : undefined
            }
            sx={{
              width: '100%',
              boxShadow: (theme) =>
                `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
              borderRadius: 2,
              backdropFilter: 'blur(8px)',
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: 1.4,
              },
              '& .MuiAlert-action, & .MuiAlert-icon': {
                paddingTop: 0,
                alignItems: 'flex-start',
                marginTop: '2px',
              },
            }}
          >
            {notification.title && (
              <Box component='article' sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.9rem' }}>
                {notification.title}
              </Box>
            )}
            {notification.message === '' && !notification.title && notification.html ? (
              <Box
                component='article'
                sx={{
                  fontSize: '0.9rem',
                  '& a': {
                    color: 'blue',
                    textDecoration: 'underline',
                    fontWeight: 500,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: notification.html }}
              />
            ) : (
              notification.message
            )}
          </Alert>
        </SnackbarItem>
      ))}
    </SnackbarContainer>
  );
}
